import { Component, inject, HostListener, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { combineLatest, switchMap } from 'rxjs';


import { AuthService } from './core/services/auth.service';
import { FamilyService } from './core/services/family.service';
import { UserService } from './core/services/user.service';
import { ExpenseEntryDialogComponent } from './features/home/expense-entry-dialog.component';
import { ExpenseService } from './core/services/expense.service';
import { TripService } from './core/services/trip.service';
import { LoadingService } from './core/services/loading.service';
import { UpdateService } from './core/services/update.service';
import { PushNotificationService } from './core/services/push-notification.service';
import { UiVersionService } from './core/services/ui-version.service';
import { ToastComponent } from './shared/components/toast/toast.component';
import packageJson from '../../package.json';



@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressBarModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  version = packageJson.version;
  authService = inject(AuthService);
  familyService = inject(FamilyService);
  userService = inject(UserService);
  expenseService = inject(ExpenseService);
  tripService = inject(TripService);
  loadingService = inject(LoadingService);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  updateService = inject(UpdateService);
  pushNotifService = inject(PushNotificationService);
  ngZone = inject(NgZone);
  uiVersion = inject(UiVersionService);



  showMenu = false;
  deferredPrompt: any;
  isOffline = !navigator.onLine;

  // Flag to prevent multiple import/open calls for profile dialog
  isProfileDialogOpening = false;

  // True for the modern shells (V2 and V3); only these render without the legacy V1 chrome.
  isV2Route(): boolean {
    const url = this.router.url;
    return url.startsWith('/v2') || url.startsWith('/v3') || url.startsWith('/login') || url === '/';
  }

  // Returns true if we are currently in the V1/Legacy shell routes
  isLegacyRoute(): boolean {
    return !this.isV2Route();
  }

  @HostListener('window:offline')
  onOffline() {
    this.isOffline = true;
    this.showToast('Warning', 'You are offline. Some features may not work.', 'warning');
  }

  @HostListener('window:online')
  onOnline() {
    this.isOffline = false;
    this.showToast('Success', 'Back online!', 'success');
  }

  retryConnection() {
    if (navigator.onLine) {
      this.isOffline = false;
      window.location.reload(); // Refresh to ensure data sync
    } else {
      this.showToast('Error', 'Still offline. Please check your connection.', 'error');
    }
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(e: any) {
    e.preventDefault();
    this.deferredPrompt = e;

    // Auto-prompt on mobile check (screen width < 768px usually denotes mobile)
    if (window.innerWidth < 768) {
      setTimeout(() => {
        this.autoPromptInstall();
      }, 3000); // 3s delay to not feel intrusive immediately
    }
  }

  private autoPromptInstall() {
    // Check if dialog already open
    if (this.dialog.openDialogs.length > 0) return;

    import('./shared/components/install-app-dialog/install-app-dialog.component')
      .then(({ InstallAppDialogComponent }) => {
        const dialogRef = this.dialog.open(InstallAppDialogComponent, {
          width: '90%',
          maxWidth: '350px',
          panelClass: 'custom-dialog-container',
          disableClose: true // Force user to choose Install or Cancel
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.installApp();
          }
        });
      });
  }

  installApp() {
    if (!this.deferredPrompt) return;
    this.showMenu = false;
    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      this.deferredPrompt = null;
    });
  }

  constructor() {
    // Start listening for FCM messages
    this.pushNotifService.listenForMessages();

    this.authService.user$.pipe(
      // Flatten the flow: When user changes, switch to profile monitoring
      switchMap(async user => {
        if (user && user.email) {
          try {
            console.log('Session Init: Step 1 - Profile check');
            const profile = await this.userService.createOrUpdateProfile(user);
            console.log('Session Init: Profile fixed:', profile?.uid);

            console.log('Session Init: Step 2 - Pending invites');
            const joined = await this.familyService.checkPendingInvites(user.email, user.uid);
            if (joined.length > 0) console.log('Session Init: Joined families:', joined);

            console.log('Session Init: Step 3 - Family Context');
            const famId = await this.familyService.ensureFamilyExists(user.uid, user.displayName || 'User');
            console.log('Session Init: Family ID Ready:', famId);

            console.log('Session Init: Step 4 - Navigation');
            if (!famId) {
              this.ngZone.run(() => {
                const redirectPath = this.isV2Route() ? '/v2/family' : '/settings';
                // Only redirect if NOT already heading to onboarding or family
                if (!this.router.url.includes('onboarding') && !this.router.url.includes('family')) {
                  this.showToast('Welcome', 'Please create a family group to start tracking expenses.', 'info');
                  this.router.navigate([redirectPath], { queryParams: { action: 'create' } });
                }
              });
            } else {
              // Redirect to dashboard if on login or root
              const currentUrl = this.router.url.split('?')[0];
              if (currentUrl === '/login' || currentUrl === '/') {
                this.ngZone.run(() => this.router.navigate([`/${this.uiVersion.version()}/dashboard`]));
              }
            }
          } catch (error: any) {
            console.error('Auth initialization error:', error);
            this.ngZone.run(() => {
              this.showToast('Error', 'Failed to initialize session: ' + (error.message || 'Unknown error'), 'error');
            });
          }

          // Check for notification permission once logged in
          this.pushNotifService.requestPermission();

          return user; // Pass user down
        }
        return null; // No user
      })
    ).subscribe();

    // SEPARATE SUBSCRIPTION for Profile Checks
    // We combine user$ and profile$ to ensure we have both
    combineLatest([this.authService.user$, this.userService.currentUserProfile$])
      .subscribe(([user, profile]) => {
        if (user && profile) {
          // CHECK PROFILE COMPLETION
          if (!profile.state || !profile.country) {
            if (this.userService.isRegistering) {
              return;
            }

            // V2 ONBOARDING REDIRECT
            if (this.isV2Route()) {
               if (this.router.url !== '/v2/onboarding') {
                  this.ngZone.run(() => this.router.navigate(['/v2/onboarding']));
               }
               return;
            }

            // V1 DIALOG FALLBACK
            // Check flags: Not opening, No dialogs currently open
            if (!this.isProfileDialogOpening && this.dialog.openDialogs.length === 0) {
              this.isProfileDialogOpening = true;
              import('./features/auth/profile-completion-dialog.component')
                .then(({ ProfileCompletionDialogComponent }) => {
                  // Double check before opening (state might have changed)
                  if (this.dialog.openDialogs.length === 0) {
                    this.dialog.open(ProfileCompletionDialogComponent, {
                      disableClose: true,
                      width: '350px'
                    });
                  }
                })
                .finally(() => {
                  this.isProfileDialogOpening = false;
                });
            }
          }
        }
      });
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  openAddExpense() {
    const contextDate = this.expenseService.getCurrentDateValue();
    const isTrip = this.expenseService.currentContextType === 'trip';

    if (isTrip && this.expenseService.currentContextId && this.expenseService.currentContextId !== 'default') {
      // NAVIGATE TO TRIP EXPENSE PAGE (Mobile Fix)
      this.router.navigate(['/trips', this.expenseService.currentContextId, 'add']);
    } else {
      // VALIDATE FAMILY BEFORE OPENING
      const profile = this.userService.currentProfileSnapshot;
      if (!profile || !profile.familyId) {
        this.showToast('Error', 'Please create or join a family first!', 'error');
        this.router.navigate(['/settings'], { queryParams: { action: 'create' } });
        return;
      }
      // NAVIGATE TO FAMILY EXPENSE PAGE (Mobile Fix)
      this.router.navigate(['/family/add']);
    }
  }

  private handleDialogClose(dialogRef: any) {
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result) {
        const user = this.authService.getCurrentUser();
        if (user) {
          try {
            await this.expenseService.addExpense({
              ...result,
              user: user.uid // Use UID for consistent member mapping
            });
            this.showToast('Success', 'Expense added successfully!', 'success');
          } catch (err: any) {
            this.showToast('Error', err.message, 'error');
          }
        }
      }
    });
  }

  navigate(path: string) {
    this.showMenu = false;

    // LEGACY REDIRECT FIX: If user is in legacy mode and clicks "Home" (empty path), 
    // we should go to /legacy instead of / (which redirects to /v2)
    let targetPath = path;
    if ((path === '' || path === '/' || path === 'home') && this.isLegacyRoute()) {
      targetPath = '/legacy';
    }

    // VALIDATION: Check Family Membership for Family Link
    if (targetPath === 'family' || targetPath === '/family') {
      const profile = this.userService.currentProfileSnapshot;
      if (!profile || !profile.familyId) {
        this.showToast('Error', 'Please create or join a family first!', 'error');
        this.router.navigate(['/settings'], { queryParams: { action: 'create' } });
        return;
      }
    }

    this.router.navigate([targetPath]);
  }

  logout() {
    this.showMenu = false;
    this.authService.logout().then(() => {
      this.router.navigate(['/login']);
    });
  }

  private showToast(title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') {
    this.snackBar.openFromComponent(ToastComponent, {
      data: { title, message, type },
      duration: 3000,
      panelClass: 'custom-toast-container',
      horizontalPosition: 'end',
      verticalPosition: 'bottom'
    });
  }
}